import React from 'react';

export type ProductMessages = Readonly<Record<string, string>>;

/** Exact, reviewed source phrases only. This never executes components or callbacks. */
export function translateProductTree<T>(value: T, messages: ProductMessages): T {
  if (typeof value === 'string') {
    const normalized = value.replace(/\s+/g, ' ').trim();
    const translated = messages[value] ?? messages[normalized];
    if (translated === undefined || translated === value || translated === normalized) return value;
    const leading = value.match(/^\s*/)?.[0] ? ' ' : '';
    const trailing = value.match(/\s*$/)?.[0] ? ' ' : '';
    return (leading + translated + trailing) as T;
  }
  if (Array.isArray(value)) {
    const reactNodes = value.every(item => item == null || React.isValidElement(item) || ['string', 'number', 'boolean'].includes(typeof item));
    return (reactNodes
      ? React.Children.map(value, item => translateProductTree(item, messages))
      : value.map(item => translateProductTree(item, messages))) as T;
  }
  if (React.isValidElement(value)) {
    const componentName = typeof value.type === 'function' ? value.type.name : '';
    if (value.type === 'code' || value.type === 'pre' || /^(CodeBlock|SyntaxHighlighter|Prism|Highlight)/.test(componentName)) return value;
    // Content marked as English (an approved quote) stays English on the Portuguese site.
    if ((value.props as { lang?: string }).lang === 'en' && typeof value.type === 'string' && value.type !== 'a') return value;
    const props: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value.props)) {
      props[key] = protectedProperty(key) ? item : translateProductTree(item, messages);
    }
    return React.cloneElement(value, props) as T;
  }
  if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [
      key, protectedProperty(key) ? item : translateProductTree(item, messages),
    ])) as T;
  }
  return value;
}

function protectedProperty(key: string): boolean {
  return /^(?:key|ref|id|anchor|slug|path|to|href|src|srcSet|url|github|repo|logo|image|code|codeRows|command|installCommand|language|className|class|style|accent|tone|color|icon|type|variant|name|value|defaultValue|htmlFor|target|rel|role|data-.+|on[A-Z].*)$/.test(key);
}

export function defineProductMessages<const T extends Record<string, string>>(portuguese: T): {
  en: { [K in keyof T]: string };
  'pt-BR': { [K in keyof T]: string };
} {
  return {
    en: Object.fromEntries(Object.keys(portuguese).map(key => [key, key])) as { [K in keyof T]: string },
    'pt-BR': portuguese,
  };
}
